import { Button } from '@/components/ui/button';
import { BlogFeaturedPost } from './blog-featured-post';
import BlogGrid from './blog-grid';
import { cmsQueryKeys } from '@/services/cms/cmsQueryKeys';
import { getBlogContent } from '@/services/cms/getBlogContent';
import { useInfiniteQuery } from '@tanstack/react-query';
import { BlogFilters } from './blog-filters';
import { useEffect, useState } from 'react';
import type { BlogPageDataResponse } from '@/services/cms/getBlogContent/schemas';
import { FullScreenLoadingSpinner } from '../full-screen-loading-spinner';

const BlogWrapper = () => {
  const [filter, setFilter] = useState<string>('all_categories');

  const [featuredPost, setFeaturedPost] = useState<BlogPageDataResponse | null>(
    null
  );

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
  } = useInfiniteQuery({
    queryKey: [cmsQueryKeys.getBlogContent[0], filter],
    initialPageParam: 1,
    queryFn: getBlogContent,
    staleTime: Infinity,
    getNextPageParam: (lastPage) => {
      if (lastPage.meta.to == null) {
        return undefined;
      }

      if (lastPage.meta.to === lastPage.meta.total) {
        return undefined;
      }

      return lastPage.meta.current_page + 1;
    },
  });

  useEffect(() => {
    if (featuredPost == null && data != null && data?.pages?.length > 0) {
      setFeaturedPost(data.pages[0].data[0]);
    }
  }, [data, featuredPost, setFeaturedPost]);

  if (featuredPost === null) {
    return (
      <div className="min-h-[58vh] flex items-center justify-center">
        <FullScreenLoadingSpinner />;
      </div>
    );
  }

  if (isError) {
    return <h1>Error</h1>;
  }

  const blogData =
    data?.pages
      .map((blog) => blog.data)
      .flat()
      .filter((blog) => blog.slug !== featuredPost.slug) ?? [];

  return (
    <div className="pt-8 md:pt-12 lg:mt-20">
      <div className="container">
        <BlogFeaturedPost blogFeaturedData={featuredPost} />
      </div>
      <div className="bg-light-grey-100 mt-8 md:mt-12 lg:mt-20 pb-0.5">
        <div className="container">
          <h2 className="pt-8 md:pt-12 lg:pt-20 text-lg md:text-xl lg:text-3xl text-black font-nunito700">
            All blog posts
          </h2>
          <div className="mt-4 lg:mt-8">
            <BlogFilters value={filter} setSelected={setFilter} />
          </div>

          <div className="mt-12 mb-12 lg:mt-[72px] lg:mb-20 min-h-[1130px] lg:min-h-[1040px]">
            {isLoading && <FullScreenLoadingSpinner />}

            {!isLoading && (
              <div className="">
                <BlogGrid
                  blogPostList={blogData}
                  isFetching={isFetchingNextPage}
                />
              </div>
            )}
          </div>

          {hasNextPage && (
            <div className="flex justify-center mb-8 md:mb-12 lg:mb-20">
              <Button
                className="h-10 w-[127px]"
                size="small"
                variant="secondary"
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogWrapper;
